import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SearchRequest } from 'gung-list';
import {
  Assortment,
  AssortmentService,
  Customer,
  gungAddRemoveSpinner,
  GungModalService,
  Product,
  TableRecord
} from 'gung-standard';
import { OperatorFunction, Observable, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, map, first } from 'rxjs';
import { Claim, ColmecClaimService } from '../../../services/colmec-claim.service';
import { ColmecProductService } from '../../../services/colmec-product.service';

@Component({
    selector: 'colmec-claim-create-modal',
    templateUrl: './colmec-claim-create-modal.component.html',
    styleUrls: ['./colmec-claim-create-modal.component.scss'],
    standalone: false
})
export class ColmecClaimCreateModalComponent implements OnInit {
  @Input()
  customer: Customer;

  @Input()
  claim?: Claim;

  orkForm: FormGroup;

  assortment: Assortment;

  public modelSearch: any;
  formatter = (product: Product) => product.id /*  + ' - ' + product.name */;
  resultFormatter = (product: Product) => product.id + ' - ' + product.name;

  search: OperatorFunction<string, readonly { id; name }[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        const terms = term.split(' ');
        const searchRequest: SearchRequest = {
          terms,
          skip: 0,
          limit: 10,
          assortment: this.assortment?.id || 's0'
        };
        return forkJoin([of(term), this.productService.getPagedProducts(searchRequest)]);
      }),
      map(([term, products]) =>
        products.items./* filter(product => new RegExp(term, 'mi').test(product.name)). */ slice(0, 10)
      )
    );

  constructor(
    public activeModal: NgbActiveModal,
    protected colmecClaimService: ColmecClaimService,
    protected formBuilder: FormBuilder,
    protected productService: ColmecProductService,
    protected assortmentService: AssortmentService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.assortmentService
      .getUserAssortment()
      .pipe(first())
      .subscribe(assortment => (this.assortment = assortment));
  }

  initForm() {
    this.orkForm = this.formBuilder.group({
      kundreklamationorsak: this.formBuilder.control(
        { value: this.claim?.extra?.ork?.kundreklamationorsak || '', disabled: false },
        [Validators.required]
      ),
      claimworkflow: this.formBuilder.control(this.claim?.extra?.ork?.claimworkflow || '', [Validators.required]),
      q_artserienr: this.formBuilder.control(this.claim?.extra?.ork?.q_artserienr || '', []),
      q_dotnr: this.formBuilder.control(this.claim?.extra?.ork?.q_dotnr || '', []),
      edit: this.formBuilder.control(this.claim?.extra?.ork?.edit || '', [Validators.required]),
      saljare: this.formBuilder.control(
        { value: (this.claim ? this.claim?.extra?.ork?.saljare : this.customer.name) || '', disabled: true },
        []
      ),
      ftgnr: this.formBuilder.control(
        { value: (this.claim ? this.claim?.extra?.ork?.ftgnr : this.customer.id) || '', disabled: true },
        []
      ),
      ordernr: this.formBuilder.control(this.claim?.extra?.ork?.ordernr || '', [this.numericValidator]),
      faktnr: this.formBuilder.control(this.claim?.extra?.ork?.faktnr || '', [this.numericValidator]),
      claimqty: this.formBuilder.control(this.claim?.extra?.ork?.claimqty || '', [Validators.required]),
      eventnote: this.formBuilder.control({ value: this.claim?.extra?.ork?.eventnote || '', disabled: true }, []),
      artnr: this.formBuilder.control({ value: this.claim?.extra?.ork?.artnr || '', disabled: true }, []),
      q_fleet: this.formBuilder.control(this.claim?.extra?.ork?.q_fleet || '', []),
      q_bosspl: this.formBuilder.control(this.claim?.extra?.ork?.q_bosspl || '', []),
      q_kompletthjul: this.formBuilder.control(this.claim?.extra?.ork?.q_kompletthjul || '', []),
      q_falg: this.formBuilder.control(this.claim?.extra?.ork?.q_falg || '', [])
    });
  }

  filterClaimWork(option: { key: string; value: string | TableRecord }): boolean {
    return option.key === '10' || option.key === '20';
  }

  onCreateClaim(element) {
    if (!this.orkForm.valid) {
      this.orkForm.markAllAsTouched();
      return;
    }
    const requestBody: Claim = {
      customerId: this.customer.id,
      extra: {
        ork: {
          ...this.orkForm.getRawValue()
        }
      }
    };
    const target = element;
    gungAddRemoveSpinner(target);
    this.colmecClaimService.postClaim(this.customer.id, requestBody).subscribe(
      result => {
        this.activeModal.close(result);
      },
      error => {
        gungAddRemoveSpinner(target);
        // this.activeModal.close(false);
      },
      () => {
        gungAddRemoveSpinner(target);
      }
    );
  }

  selectProduct({ item, preventDefault }) {
    this.orkForm.get('artnr').patchValue(item.id);
  }

  ngModelChange(item, target) {
    if (!item) {
      this.orkForm.get('artnr').patchValue('');
    }
  }

  focusout({ target }, modelSearch) {
    if (!modelSearch) {
      target.value = '';
    }
  }

  numericValidator(control: FormControl) {
    const value = control.value;
    if (isNaN(value)) {
      return { notNumeric: true };
    }
    return null;
  }
}
